import React from 'react';
import {
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
} from '@mui/material';
import AuthorsSidebar from '../../components/AuthorsSidebar';
import {
  Payment,
  Schedule,
  AccountBalance,
  Receipt,
  CheckCircle,
  Info,
} from '@mui/icons-material';

const RoyaltyPayments: React.FC = () => {
  const paymentConditions = [
    {
      title: 'Бесплатная публикация',
      description: 'Публикация статей в журнале является бесплатной для авторов',
      icon: <CheckCircle color="success" />,
    },
    {
      title: 'Отсутствие гонораров',
      description: 'Журнал не выплачивает гонорары авторам за публикацию статей',
      icon: <Info color="info" />,
    },
    {
      title: 'Открытый доступ',
      description: 'Статьи публикуются в открытом доступе без ограничений',
      icon: <Receipt color="primary" />,
    },
  ];

  const paymentPolicy = [
    'Журнал не взимает плату за публикацию статей',
    'Авторы не получают денежного вознаграждения',
    'Публикация осуществляется на безвозмездной основе',
    'Все расходы на издание покрываются за счет журнала',
  ];

  const authorBenefits = [
    'Бесплатная публикация научных работ',
    'Повышение научной репутации',
    'Участие в научном сообществе',
    'Распространение результатов исследований',
  ];

  const financialAspects = [
    {
      aspect: 'Публикация',
      cost: 'Бесплатно',
      description: 'Для авторов',
    },
    {
      aspect: 'Рецензирование',
      cost: 'Бесплатно',
      description: 'Для авторов',
    },
    {
      aspect: 'Доступ к статьям',
      cost: 'Бесплатно',
      description: 'Для читателей',
    },
    {
      aspect: 'Печатные экземпляры',
      cost: 'По запросу',
      description: 'Ограниченное количество',
    },
  ];

  return (
    <AuthorsSidebar>
      <Container sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Выплаты гонораров
      </Typography>
      
      <Typography variant="body1" paragraph>
        Данный раздел содержит информацию о финансовых аспектах публикации в журнале 
        и политике выплат.
      </Typography>

      <Alert severity="info" sx={{ mb: 4 }}>
        Журнал придерживается принципов открытого доступа и не взимает плату с авторов за публикацию.
      </Alert>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Условия выплат
        </Typography>
        <Grid container spacing={2}>
          {paymentConditions.map((condition, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {condition.icon}
                    <Typography variant="h6" sx={{ ml: 1 }}>
                      {condition.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {condition.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Политика журнала
        </Typography>
        <Typography variant="body1" paragraph>
          Журнал следует принципам открытого доступа и не взимает плату с авторов за публикацию статей.
        </Typography>
        <List>
          {paymentPolicy.map((policy, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText primary={policy} />
            </ListItem>
          ))}
        </List>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Преимущества для авторов
        </Typography>
        <Typography variant="body1" paragraph>
          Хотя авторы не получают денежного вознаграждения, публикация в журнале 
          предоставляет множество других преимуществ.
        </Typography>
        <List>
          {authorBenefits.map((benefit, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <Payment color="primary" />
              </ListItemIcon>
              <ListItemText primary={benefit} />
            </ListItem>
          ))}
        </List>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Финансовые аспекты
        </Typography>
        <Typography variant="body1" paragraph>
          Подробная информация о стоимости различных услуг журнала.
        </Typography>
        
        <Grid container spacing={2}>
          {financialAspects.map((aspect, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom>
                    {aspect.aspect}
                  </Typography>
                  <Chip 
                    label={aspect.cost} 
                    color={aspect.cost === 'Бесплатно' ? 'success' : 'primary'}
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {aspect.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Сроки и процедуры
        </Typography>
        <Typography variant="body1" paragraph>
          Поскольку журнал не выплачивает гонорары, специальные сроки и процедуры 
          выплат не применяются.
        </Typography>
        
        <Box sx={{ mt: 3, p: 2, backgroundColor: 'success.light', borderRadius: 1 }}>
          <Typography variant="body2" color="success.contrastText">
            <strong>Важно:</strong> Все услуги журнала (публикация, рецензирование, 
            размещение в открытом доступе) предоставляются авторам бесплатно.
          </Typography>
        </Box>
      </Paper>
      </Container>
    </AuthorsSidebar>
  );
};

export default RoyaltyPayments;
